import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, CreditCard, FlaskConical, PlugZap, Wallet } from "lucide-react";

const CHIPS = [50, 100, 200, 500, 1000];
const PKG = { 50: "sar_50", 100: "sar_100", 200: "sar_200", 500: "sar_500", 1000: "sar_1000" };

export default function AddMoneyModal({ open, onClose }) {
  const { t, lang } = useApp();
  const [cfg, setCfg] = useState(null);
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(null);
  const [moyasarIntent, setMoyasarIntent] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMoyasarIntent(null); setCfg(null); setBusy(null);
    api.get("/payments/config").then((r) => {
      setCfg(r.data);
      setMethod(r.data.provider === "moyasar" ? "card" : r.data.paypal_enabled ? "paypal" : "card");
    }).catch(() => setCfg({ provider: "none" }));
  }, [open]);

  useEffect(() => {
    if (!moyasarIntent || !window.Moyasar) return;
    window.Moyasar.init({
      element: ".mysr-form",
      amount: moyasarIntent.amount_halalas,
      currency: "SAR",
      description: "SB Pay wallet top-up",
      publishable_api_key: moyasarIntent.publishable_key,
      callback_url: `${window.location.origin}/payment/success?provider=moyasar&topup_id=${moyasarIntent.topup_id}`,
      methods: ["creditcard"],
      supported_networks: ["mada", "visa", "mastercard"],
      metadata: { topup_id: moyasarIntent.topup_id },
    });
  }, [moyasarIntent]);

  const cardOn = cfg && cfg.provider === "moyasar";
  const paypalOn = cfg && cfg.paypal_enabled;
  const isTest = cfg && (cfg.mode === "test" || cfg.paypal_mode === "sandbox");

  const pay = async (amt) => {
    setBusy(amt);
    try {
      if (method === "paypal") {
        const r = await api.post("/payments/paypal/order", { package_id: PKG[amt], origin_url: window.location.origin });
        if (!r.data.approve_url) throw new Error();
        window.location.href = r.data.approve_url;
        return;
      }
      const r = await api.post("/payments/moyasar/intent", { package_id: PKG[amt], origin_url: window.location.origin });
      setMoyasarIntent(r.data);
      setBusy(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
      setBusy(null);
    }
  };

  const fmt = (n) => n.toLocaleString(lang === "ar" ? "ar-SA" : "en-US");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-money-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-primary" /> {t.addMoney}</DialogTitle>
          <DialogDescription>{t.addMoneyDesc}</DialogDescription>
        </DialogHeader>

        {!cfg && <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

        {cfg && !cardOn && !paypalOn && (
          <div data-testid="topup-not-configured" className="py-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted grid place-items-center mx-auto mb-3"><PlugZap className="w-6 h-6 text-muted-foreground" /></div>
            <p className="font-bold">{t.topupNotConfigured}</p>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{t.topupNotConfiguredDesc}</p>
          </div>
        )}

        {cfg && (cardOn || paypalOn) && (
          moyasarIntent ? <div className="mysr-form" data-testid="moyasar-form" /> : (
            <div className="space-y-3">
              {isTest && (
                <div data-testid="topup-test-banner" className="flex items-start gap-2 rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-700 dark:text-amber-300">
                  <FlaskConical className="w-4 h-4 shrink-0 mt-0.5" /><span>{t.testModeNote}</span>
                </div>
              )}
              {cardOn && paypalOn && (
                <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-muted">
                  {[["card", CreditCard, t.payByCard], ["paypal", Wallet, t.payWithPayPal]].map(([k, I, label]) => (
                    <button key={k} data-testid={`topup-method-${k}`} onClick={() => setMethod(k)}
                      className={`h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${method === k ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
                      <I className="w-4 h-4" />{label}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-sm text-muted-foreground">{method === "paypal" ? t.paypalNote : t.cardTopupDesc}</p>
              <div className="grid grid-cols-2 gap-2.5">
                {CHIPS.map((c) => (
                  <button key={c} data-testid={`card-pkg-${c}`} onClick={() => pay(c)} disabled={busy !== null}
                    className="flex items-center justify-center gap-1 h-14 rounded-2xl bg-muted hover:bg-primary/10 border border-transparent hover:border-primary/30 font-bold tabular-nums transition-all active:scale-95 disabled:opacity-60 last:col-span-2">
                    {busy === c ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{fmt(c)} <span className="text-xs text-muted-foreground">{t.currency}</span></>}
                  </button>
                ))}
              </div>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
